import React from "react";
import Button from "components/button/Button";
import CommonModal from "components/modals/Modal";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import SwitchField from "components/fields/SwitchField";
import NumFormatField from "components/fields/NumFormatField";
import { ModalBody, ModalCloseButton, ModalHeader } from "@chakra-ui/modal";
import { Controller, FieldValues, useForm, useWatch } from "react-hook-form";
import { IDynamicFees, IDynamicFeesCreate } from "../types/fees.interface";
import useFeesTable from "../hooks/useFeesTable";
import SelectField from "components/fields/SelectField";

const FeeTypes: {
  value: string;
  label: string;
}[] = [
  {
    value: "fixed",
    label: "Nominal",
  },
  {
    value: "percentage",
    label: "Persentase",
  },
];

interface ICourierServiceForm {
  item?: IDynamicFees;
  isOpen: boolean;
  onClose: () => void;
}

const DynamicFeeForm: React.FC<ICourierServiceForm> = (props) => {
  const { item, isOpen, onClose } = props;
  const { onSubmit, onUpdate, getDynamicFeesTableData } = useFeesTable();
  const [isLoading, setIsLoading] = React.useState(false);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const watchFeesType = useWatch({
    control,
    name: "type",
  });

  const onSubmitForm = async (data: FieldValues) => {
    let promise: Promise<any>;
    const type = data.type as { value: string; label: string };
    const payload: IDynamicFeesCreate = {
      ...data,
      type: type.value,
    } as IDynamicFeesCreate;
    setIsLoading(true);

    if (item) promise = onUpdate(item.id, payload);
    else promise = onSubmit(payload);

    promise
      .then(() => {
        getDynamicFeesTableData();
        setIsLoading(false);
        onClose();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    if (item)
      reset({
        ...item,
        type: FeeTypes.find((type) => type.value === item.type),
      });
    else
      reset({
        is_active: false,
        name: "",
        type: "",
        value: "",
        description: "",
      });

    // eslint-disable-next-line
  }, [item]);

  return (
    <CommonModal
      size="3xl"
      children={
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <ModalHeader>Tambah Layanan Baru</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SwitchField
              label="Status Layanan Aktif"
              desc=""
              id="is_active"
              mb="mb-3"
              mt=""
              placeholder="Status Layanan Aktif"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
            <InputField
              variant="outline"
              extra="mb-3"
              label="Nama Biaya"
              placeholder="Masukkan Nama Biaya"
              id="name"
              type="text"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Nama Biaya harus diisi!",
              }}
            />
            <div className="mb-3 grid grid-cols-12 gap-4">
              <Controller
                name="type"
                control={control}
                rules={{
                  required: "Bentuk biaya harus diisi!",
                }}
                render={({ field }) => (
                  <SelectField
                    field={field}
                    errors={errors}
                    label="Bentuk biaya"
                    id="type"
                    placeholder="Pilih Bentuk biaya"
                    extra="col-span-12 lg:col-span-6"
                    options={FeeTypes}
                    optionLabel="label"
                    optionValue="value"
                    rules={{
                      required: "Bentuk biaya harus dipilih!",
                    }}
                    variant="outline"
                  />
                )}
              />
              <Controller
                name="value"
                control={control}
                rules={{ required: "Nominal harus diisi!" }}
                render={({ field }) => (
                  <NumFormatField
                    field={field}
                    extra="col-span-12 lg:col-span-6"
                    decimalSeparator=","
                    thousandSeparator={
                      watchFeesType?.value === "fixed" ? "." : ""
                    }
                    errors={errors}
                    rules={{ required: "Nominal harus diisi!" }}
                    label="Nominal"
                    id="value"
                    placeholder="Masukkan Nominal"
                    variant="outline"
                    disabled={watchFeesType === ""}
                    prefix={watchFeesType?.value === "fixed" ? "Rp" : ""}
                    suffix={watchFeesType?.value === "percentage" ? "%" : ""}
                  />
                )}
              />
            </div>
            <TextField
              extra="mb-3 flex-1 mt-10 lg:mt-0"
              label="Deskripsi"
              placeholder="Deskripsi kurir"
              id="description"
              variant="outline"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
            <Button
              children="Simpan"
              size="fit"
              variant="primary"
              extra="my-5 float-right"
              isLoading={isLoading}
            />
          </ModalBody>
        </form>
      }
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default DynamicFeeForm;
